import { useState, useCallback, useMemo } from "react"
import {Droplets, Sparkles} from 'lucide-react'
import ExportButtons from "./ExportButtons"
import SaveHistoryButton from "./SaveHistoryButton"
import type { HistoryPayload } from "@/hooks/useCalculationHistory"

export default function WaterIntakeCalculator() {
  const [weight, setWeight] = useState("")
  const [unit, setUnit] = useState<"kg" | "lbs">("kg")
  const [exerciseMinutes, setExerciseMinutes] = useState(30)
  const [result, setResult] = useState<{ liters: number; glasses: number } | null>(null)

  const calculate = useCallback(() => {
    const w = parseFloat(weight)
    if (isNaN(w) || w <= 0) return

    const weightKg = unit === "kg" ? w : w * 0.453592
    // Base: 35ml per kg + 500ml per 30 min exercise
    const baseMl = weightKg * 35
    const exerciseMl = (exerciseMinutes / 30) * 500
    const totalMl = baseMl + exerciseMl
    const liters = totalMl / 1000
    const glasses = totalMl / 250

    setResult({
      liters: Math.round(liters * 10) / 10,
      glasses: Math.round(glasses),
    })
  }, [weight, unit, exerciseMinutes])

  const historyPayload = useMemo<HistoryPayload | null>(() => {
    if (!result) return null
    return {
      calculatorType: "water",
      summary: `Water ${result.liters} L/day · ${result.glasses} glasses`,
      details: {
        weight,
        unit,
        exerciseMinutes,
        liters: result.liters,
        glasses: result.glasses,
      },
    }
  }, [result, weight, unit, exerciseMinutes])

  const exportRows = useMemo(() => {
    if (!result) return []
    return [
      { label: "Daily Water Intake", value: `${result.liters} liters` },
      { label: "Glasses Per Day", value: `${result.glasses} glasses (250ml each)` },
      { label: "Body Weight", value: `${weight} ${unit}` },
      { label: "Daily Exercise", value: `${exerciseMinutes} minutes` },
      { label: "Formula", value: "35ml/kg + 500ml per 30 min exercise" },
    ]
  }, [result, weight, unit, exerciseMinutes])

  return (
    <section id="water" className="py-16 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="relative bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50 rounded-3xl border border-sky-200/60 shadow-premium overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Left - Info */}
            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 border border-sky-200 mb-5 w-fit shadow-sm">
                <Droplets className="w-3.5 h-3.5 text-sky-600" />
                <span className="text-xs font-semibold text-sky-700 uppercase tracking-wide">Bonus Tool</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4 tracking-tight">
                Water Intake Calculator for Weight Loss
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Hydration is the silent partner of fat loss. Proper water intake boosts metabolism,
                reduces hunger, and helps your body metabolize stored fat more efficiently.
                Use this quick calculator to find your optimal daily water intake.
              </p>

              {/* Result Display */}
              {result && (
                <div className="bg-white rounded-xl border border-sky-200 p-5 shadow-premium animate-fade-in-up">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-sky-500" />
                      <span className="text-sm font-semibold text-slate-700">Your Daily Water Intake</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <SaveHistoryButton payload={historyPayload} variant="light" />
                      <ExportButtons
                        title="Water Intake Calculator Results"
                        subtitle={`${weight} ${unit} | ${exerciseMinutes} min exercise/day`}
                        rows={exportRows}
                        accentColor="sky"
                        note="Proper hydration supports metabolism and fat loss. Adjust intake based on climate, health conditions, and individual needs."
                      />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <div>
                      <span className="text-3xl font-extrabold text-sky-600">{result.liters}L</span>
                      <span className="text-sm text-slate-500 ml-1">liters</span>
                    </div>
                    <div className="w-px h-8 bg-gray-200" />
                    <div>
                      <span className="text-3xl font-extrabold text-sky-600">{result.glasses}</span>
                      <span className="text-sm text-slate-500 ml-1">glasses</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Based on 250ml per glass</p>
                </div>
              )}
            </div>

            {/* Right - Calculator */}
            <div className="p-8 sm:p-10 lg:p-12 bg-white/60 backdrop-blur-sm">
              <div className="space-y-5">
                {/* Weight */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Body Weight</label>
                    <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                      {(["kg", "lbs"] as const).map((u) => (
                        <button
                          key={u}
                          onClick={() => setUnit(u)}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all duration-200 ${
                            unit === u ? "bg-white text-sky-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder={unit === "kg" ? "e.g. 75 kg" : "e.g. 165 lbs"}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
                  />
                </div>

                {/* Exercise */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Daily Exercise: <span className="text-sky-600">{exerciseMinutes} min</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="120"
                    step="5"
                    value={exerciseMinutes}
                    onChange={(e) => setExerciseMinutes(parseInt(e.target.value))}
                    className="w-full"
                    style={{
                      background: `linear-gradient(to right, #0ea5e9 0%, #0ea5e9 ${(exerciseMinutes / 120) * 100}%, #e2e8f0 ${(exerciseMinutes / 120) * 100}%, #e2e8f0 100%)`,
                    }}
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>0 min</span>
                    <span>60 min</span>
                    <span>120 min</span>
                  </div>
                </div>

                {/* Calculate */}
                <button
                  onClick={calculate}
                  disabled={!weight}
                  className="w-full py-3.5 bg-gradient-to-br from-sky-500 to-sky-600 text-white font-bold rounded-xl hover:from-sky-600 hover:to-sky-700 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-sky-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:from-sky-500 disabled:hover:to-sky-600 active:scale-[0.98]"
                >
                  Calculate Water Intake
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
